import React, { useState } from 'react';
import {
  Container,
  Paper,
  Box,
  TextField,
  Button,
  Typography,
  Alert,
  CircularProgress,
} from '@mui/material';
import AdminPanelSettingsIcon from '@mui/icons-material/AdminPanelSettings';
import LockIcon from '@mui/icons-material/Lock';
import { useAuth } from '../contexts/AuthContext';
import { getLoginErrorMessage, get2FAErrorMessage, LoginErrorField } from '../utils/loginErrors';

const LoginPage: React.FC = () => {
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [errorField, setErrorField] = useState<LoginErrorField>(null);

  // 2FA state
  const [requires2FA, setRequires2FA] = useState(false);
  const [tempToken, setTempToken] = useState('');
  const [twoFactorCode, setTwoFactorCode] = useState('');

  const { login, verify2FA, sessionExpiredMessage, clearSessionExpiredMessage } = useAuth();

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setLoading(true);
    setError('');
    setErrorField(null);

    try {
      const result = await login(email, password, 'admin');

      if (result.requires2FA && result.tempToken) {
        setRequires2FA(true);
        setTempToken(result.tempToken);
        setLoading(false);
        return;
      }

      // Navigation will be handled by App.tsx based on role
    } catch (err: unknown) {
      const info = getLoginErrorMessage(err);
      setError(info.message);
      setErrorField(info.field);
    } finally {
      setLoading(false);
    }
  };

  const handle2FASubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setLoading(true);
    setError('');

    try {
      await verify2FA(tempToken, twoFactorCode, 'admin');
    } catch (err: unknown) {
      setError(get2FAErrorMessage(err));
    } finally {
      setLoading(false);
    }
  };

  const handleBackToLogin = () => {
    setRequires2FA(false);
    setTempToken('');
    setTwoFactorCode('');
    setError('');
    setErrorField(null);
  };

  // 2FA Verification Form
  if (requires2FA) {
    return (
      <Container component="main" maxWidth="sm">
        <Box
          sx={{
            marginTop: 8,
            display: 'flex',
            flexDirection: 'column',
            alignItems: 'center',
          }}
        >
          <Paper elevation={3} sx={{ padding: 4, width: '100%', borderRadius: 3 }}>
            <Box
              sx={{
                display: 'flex',
                flexDirection: 'column',
                alignItems: 'center',
              }}
            >
              <Box
                sx={{
                  width: 60,
                  height: 60,
                  borderRadius: '50%',
                  backgroundColor: 'warning.light',
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  mb: 2,
                }}
              >
                <LockIcon sx={{ fontSize: 32, color: 'warning.dark' }} />
              </Box>
              
              <Typography component="h1" variant="h4" gutterBottom fontWeight="bold">
                Two-Factor Authentication
              </Typography>
              <Typography variant="body1" color="text.secondary" gutterBottom textAlign="center">
                Enter the 6-digit code from your authenticator app
              </Typography>
              
              {error && (
                <Alert severity="error" sx={{ width: '100%', mb: 2, mt: 2 }}>
                  {error}
                </Alert>
              )}
              
              <Box component="form" onSubmit={handle2FASubmit} sx={{ mt: 2, width: '100%' }}>
                <TextField
                  margin="normal"
                  required
                  fullWidth
                  id="twoFactorCode"
                  label="Verification Code"
                  name="twoFactorCode"
                  autoComplete="one-time-code"
                  autoFocus
                  value={twoFactorCode}
                  onChange={(e) => setTwoFactorCode(e.target.value.replace(/\D/g, '').slice(0, 6))}
                  disabled={loading}
                  inputProps={{ 
                    maxLength: 6,
                    style: { textAlign: 'center', fontSize: '1.5rem', letterSpacing: '0.5rem' }
                  }}
                  placeholder="000000"
                />
                <Button
                  type="submit"
                  fullWidth
                  variant="contained"
                  sx={{ mt: 3, mb: 2, py: 1.5 }}
                  disabled={loading || twoFactorCode.length !== 6}
                >
                  {loading ? (
                    <CircularProgress size={24} color="inherit" />
                  ) : (
                    'Verify'
                  )}
                </Button>
                <Button
                  fullWidth
                  variant="text"
                  onClick={handleBackToLogin}
                  disabled={loading}
                >
                  Back to Login
                </Button>
              </Box>
            </Box>
          </Paper>
        </Box>
      </Container>
    );
  }

  // Normal Login Form
  return (
    <Container component="main" maxWidth="sm">
      <Box
        sx={{
          marginTop: 8,
          display: 'flex',
          flexDirection: 'column',
          alignItems: 'center',
        }}
      >
        <Paper elevation={3} sx={{ padding: 4, width: '100%', borderRadius: 3 }}>
          <Box
            sx={{
              display: 'flex',
              flexDirection: 'column',
              alignItems: 'center',
            }}
          >
            <Box
              sx={{
                width: 60,
                height: 60,
                borderRadius: '50%',
                backgroundColor: 'primary.light',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                mb: 2,
              }}
            >
              <AdminPanelSettingsIcon sx={{ fontSize: 32, color: 'primary.main' }} />
            </Box>
            
            <Typography component="h1" variant="h4" gutterBottom fontWeight="bold">
              Administrator Portal
            </Typography>
            <Typography variant="body1" color="text.secondary" gutterBottom>
              Sign in to manage the system
            </Typography>

            {sessionExpiredMessage && !error && (
              <Alert
                severity="warning"
                sx={{ width: '100%', mb: 2, mt: 2 }}
                onClose={clearSessionExpiredMessage}
              >
                {sessionExpiredMessage}
              </Alert>
            )}

            {error && (
              <Alert severity="error" sx={{ width: '100%', mb: 2, mt: 2 }}>
                {error}
              </Alert>
            )}
            
            <Box component="form" onSubmit={handleSubmit} sx={{ mt: 2, width: '100%' }}>
              <TextField
                margin="normal"
                required
                fullWidth
                id="email"
                label="Email Address"
                name="email"
                autoComplete="email"
                autoFocus
                value={email}
                onChange={(e) => {
                  setEmail(e.target.value);
                  if (errorField === 'email') setErrorField(null);
                }}
                disabled={loading}
                error={errorField === 'email'}
                helperText={errorField === 'email' ? error : undefined}
              />
              <TextField
                margin="normal"
                required
                fullWidth
                name="password"
                label="Password"
                type="password"
                id="password"
                autoComplete="current-password"
                value={password}
                onChange={(e) => {
                  setPassword(e.target.value);
                  if (errorField === 'password') setErrorField(null);
                }}
                disabled={loading}
                error={errorField === 'password'}
                helperText={errorField === 'password' ? error : undefined}
              />
              <Button
                type="submit"
                fullWidth
                variant="contained"
                sx={{ mt: 3, mb: 2, py: 1.5 }}
                disabled={loading || !email || !password}
              >
                {loading ? (
                  <CircularProgress size={24} color="inherit" />
                ) : (
                  'Sign In'
                )}
              </Button>
            </Box>
          </Box>
        </Paper>
      </Box>
    </Container>
  );
};

export default LoginPage;
